import React from 'react';

import { PlusIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { WithDataTestid } from '@vlabs/uikit/types';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { CreateStreamForm } from './CreateStreamForm';
import { useCreateStream } from './useCreateStream';
import { useCreateStreamTranslation } from '../i18n';
import type { CreateStreamProps } from '../types';

const displayName = 'CreateStreamButton';
const testid = buildTestid(displayName);

interface CreateStreamButtonProps extends
  CreateStreamProps,
  WithDataTestid,
  Omit<ButtonProps, 'onClick'> {}

function CreateStreamButton({
  groupId,
  onCreateStream = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: CreateStreamButtonProps): React.JSX.Element {
  const { t } = useCreateStreamTranslation();
  const modal = useModal({ size: 'small', scrollable: true });
  const createStream = useCreateStream({
    onCreateStream,
    onSuccess: modal.close,
  });

  return (
    <React.Fragment>
      {modal.wrap(
        <CreateStreamForm
          groupId={groupId}
          isPending={createStream.isPending}
          onCancel={modal.close}
          onSubmit={createStream.onSubmit}
        />,
      )}

      <Button
        icon={<PlusIcon />}
        kind="secondary"
        onClick={modal.open}
        size="small"
        variant="outlined"
        {...testid(dataTestId)}
        {...props}
      >
        {t('title')}
      </Button>
    </React.Fragment>
  );
}

CreateStreamButton.displayName = displayName;

export { CreateStreamButton };
